import { Navigate, useLocation } from "react-router-dom";
import { Loader2, ShieldOff } from "lucide-react";
import { useAuthStore } from "@/stores/authStore";
import { canAny } from "@/lib/permissions";
import { EmptyState } from "@/components/ui/primitives";
import { Button } from "@/components/ui/button";

export const FullPageSpinner = ({ label = "Loading" }: { label?: string }) => (
  <div
    className="grid min-h-screen place-items-center bg-background"
    role="status"
    aria-live="polite"
  >
    <div className="flex flex-col items-center gap-3">
      <Loader2 className="h-6 w-6 animate-spin text-primary" aria-hidden />
      <p className="text-sm text-muted-foreground">{label}…</p>
    </div>
  </div>
);

export const RequireAuth = ({ children }: { children: React.ReactNode }) => {
  const status = useAuthStore((s) => s.status);
  const location = useLocation();

  if (status === "loading") return <FullPageSpinner label="Checking your session" />;
  if (status === "anonymous") {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  return <>{children}</>;
};

/**
 * Renders a 403 panel rather than redirecting, so an admin understands they lack
 * a permission instead of thinking the page is broken.
 */
export const RequirePermission = ({
  permissions,
  children,
}: {
  permissions: string[];
  children: React.ReactNode;
}) => {
  const granted = useAuthStore((s) => s.permissions);
  const roleKey = useAuthStore((s) => s.roleKey);

  if (!canAny(granted, permissions)) {
    return (
      <EmptyState
        icon={ShieldOff}
        title="You do not have access to this area"
        description={`Your role (${roleKey ?? "unknown"}) is missing ${permissions.join(" or ")}. Ask a super admin if you need it.`}
        action={
          <Button variant="outline" size="sm" asChild>
            <a href="/">Back to dashboard</a>
          </Button>
        }
        className="mt-8"
      />
    );
  }
  return <>{children}</>;
};

/** Keeps an authenticated admin off /login. */
export const RedirectIfAuthenticated = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const status = useAuthStore((s) => s.status);
  if (status === "loading") return <FullPageSpinner label="Checking your session" />;
  if (status === "authenticated") return <Navigate to="/" replace />;
  return <>{children}</>;
};
